import { z } from 'zod';

/** Общая граница площадки и бюджетной проекции: машина даёт работу и простой. */
export const SITE_MAX_VALUATION_RESOURCES = 1000;

/** Десятичные количества: без округления через IEEE-754 и без денежных полей. */
export const SiteQuantity = z
  .string()
  .regex(
    /^(0|[1-9]\d{0,14})(\.\d{1,6})?$/,
    'Укажите неотрицательное количество, не более 6 знаков после запятой',
  );
export const SiteDate = z.iso.date();
const Id = z.uuid();
const Note = z.string().trim().max(2000);
const Revision = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);

export function siteQuantityUnits(value: string): bigint {
  SiteQuantity.parse(value);
  const [whole, fraction = ''] = value.split('.');
  return BigInt(whole!) * 1_000_000n + BigInt(fraction.padEnd(6, '0'));
}

const SourceRef = z.strictObject({ type: z.string().min(1).max(80), id: Id, revision: Revision });
export const SiteLabor = z
  .strictObject({
    id: Id,
    workerId: Id.nullable(),
    profession: z.string().trim().min(1).max(200),
    headcount: z.number().int().min(1).max(10000),
    /** Всего человеко-часов, включая простой. */
    hours: SiteQuantity,
    idleHours: SiteQuantity,
    idleReason: Note.nullable(),
  })
  .superRefine((row, ctx) => {
    if (!SiteQuantity.safeParse(row.hours).success || !SiteQuantity.safeParse(row.idleHours).success) return;
    if (row.workerId && row.headcount !== 1)
      ctx.addIssue({
        code: 'custom',
        message: 'Для одного работника численность равна 1',
        path: ['headcount'],
      });
    if (siteQuantityUnits(row.hours) > BigInt(row.headcount) * 24_000_000n)
      ctx.addIssue({ code: 'custom', message: 'Больше 24 часов на человека', path: ['hours'] });
    if (siteQuantityUnits(row.idleHours) > siteQuantityUnits(row.hours))
      ctx.addIssue({ code: 'custom', message: 'Простой превышает общее время', path: ['idleHours'] });
    if (siteQuantityUnits(row.idleHours) > 0n && !row.idleReason)
      ctx.addIssue({ code: 'custom', message: 'Укажите причину простоя', path: ['idleReason'] });
  });
export const SiteMachine = z
  .strictObject({
    id: Id,
    machineId: Id,
    /** Работа и простой заданы раздельно. */
    hours: SiteQuantity,
    idleHours: SiteQuantity,
    idleReason: Note.nullable(),
    telemetryRef: SourceRef.nullable(),
  })
  .superRefine((row, ctx) => {
    if (!SiteQuantity.safeParse(row.hours).success || !SiteQuantity.safeParse(row.idleHours).success) return;
    if (siteQuantityUnits(row.hours) + siteQuantityUnits(row.idleHours) > 24_000_000n)
      ctx.addIssue({ code: 'custom', message: 'Работа и простой машины превышают 24 часа', path: ['hours'] });
    if (siteQuantityUnits(row.idleHours) > 0n && !row.idleReason)
      ctx.addIssue({ code: 'custom', message: 'Укажите причину простоя', path: ['idleReason'] });
  });
export const SiteMaterial = z.strictObject({
  id: Id,
  warehouseId: Id,
  itemId: Id,
  quantity: SiteQuantity,
  unit: z.string().trim().min(1).max(40),
  normRef: SourceRef.nullable(),
  issueAllocations: z.array(z.strictObject({ documentLineId: Id, quantity: SiteQuantity })).max(100),
});
export const SiteReportLine = z.strictObject({
  id: Id,
  activityId: Id,
  packageItemId: Id,
  unit: z.string().trim().min(1).max(40),
  plannedQuantity: SiteQuantity.nullable(),
  claimedQuantity: SiteQuantity,
  ruleRef: SourceRef.nullable(),
  stepIds: z.array(Id).max(100),
  labor: z.array(SiteLabor).max(1000),
  machines: z.array(SiteMachine).max(200),
  materials: z.array(SiteMaterial).max(1000),
  note: Note,
});
/** Место съёмки в черновике (ПР-03, МП-07): только при явном согласии, без миграции. */
export const SitePhotoLocation = z.strictObject({
  lat: z.number().finite().min(-90).max(90),
  lng: z.number().finite().min(-180).max(180),
  accuracyM: z.number().finite().positive().max(50_000).nullable(),
  at: z.iso.datetime({ offset: true }),
});
export type SitePhotoLocation = z.infer<typeof SitePhotoLocation>;

/** Вид вложения к работе отчёта: фото или короткий ролик (ПР-03, МП-03). */
export const SITE_MEDIA_KINDS = ['photo', 'video'] as const;
export const SiteMediaKind = z.enum(SITE_MEDIA_KINDS);

export const SitePhoto = z
  .strictObject({
    clientPhotoId: Id,
    lineId: Id,
    fileId: Id.nullable(),
    size: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    caption: Note,
    capturedAt: z.iso.datetime({ offset: true }).nullable(),
    source: z.enum(['camera', 'gallery']),
    /** Фото (по умолчанию) или видео; старые черновики без поля — фото. */
    kind: SiteMediaKind.default('photo'),
    /** MIME ролика (video/mp4 и т.п.); для фото — null. */
    mimeType: z.string().max(100).nullable().default(null),
    /** Согласие сохранить место только в этой записи (МП-07); фоном не следим. */
    locationConsent: z.boolean(),
    /** Координаты — только при locationConsent; иначе null. */
    location: SitePhotoLocation.nullable().default(null),
    /** Лист ПСД своего проекта, к которому относится снимок (ПР-03); без миграции — в JSON черновика */
    sheetId: Id.nullable().default(null),
    /** Замечание / предписание своего проекта (ПР-03) */
    remarkId: Id.nullable().default(null),
  })
  .superRefine((photo, ctx) => {
    if (photo.location && !photo.locationConsent) {
      ctx.addIssue({
        code: 'custom',
        message: 'Место съёмки можно сохранить только с согласия',
        path: ['location'],
      });
    }
    if (photo.kind === 'video' && !photo.mimeType?.startsWith('video/')) {
      ctx.addIssue({
        code: 'custom',
        message: 'У видео должен быть тип video/*',
        path: ['mimeType'],
      });
    }
    if (photo.kind === 'photo' && photo.mimeType) {
      ctx.addIssue({
        code: 'custom',
        message: 'У фото тип файла не указывается',
        path: ['mimeType'],
      });
    }
  });

/** Погода смены (ПР-02): вручную или подстановка из Open-Meteo (`GET …/weather`). */
export const SITE_WEATHER_CONDITIONS = ['clear', 'cloudy', 'rain', 'snow', 'wind', 'fog', 'other'] as const;
export const SiteWeatherCondition = z.enum(SITE_WEATHER_CONDITIONS);
export const SiteWeather = z.strictObject({
  temperatureC: z.number().int().min(-60).max(60).nullable(),
  condition: SiteWeatherCondition.nullable(),
  windMs: z.number().int().min(0).max(70).nullable(),
  note: Note.nullable(),
});
export type SiteWeather = z.infer<typeof SiteWeather>;

/** Показания телематики за день для подстановки в отчёт смены (ПР-02 ← РС-16). */
export const SiteTelemetryReading = z.strictObject({
  id: Id,
  machineId: Id,
  machineName: z.string().min(1).max(200),
  regNumber: z.string().max(40).nullable(),
  day: SiteDate,
  hours: z.number().nullable(),
  idleHours: z.number().nullable(),
  fuelLitres: z.number().nullable(),
  distanceKm: z.number().nullable(),
  source: z.enum(['iso15143', 'csv']),
});
export type SiteTelemetryReading = z.infer<typeof SiteTelemetryReading>;

export const SiteReportDraft = z
  .strictObject({
    schemaVersion: z.literal(1),
    projectId: Id,
    objectId: Id.nullable(),
    packageId: Id,
    reportDay: SiteDate,
    projectTimeZone: z.string().min(1).max(80),
    shiftKey: z.string().trim().min(1).max(80),
    planSnapshotId: Id,
    scheduleRevision: Revision,
    formVersion: z.literal(1),
    lines: z.array(SiteReportLine).max(200),
    photos: z.array(SitePhoto).max(100),
    note: Note,
    /** Старые черновики без поля — считаем «не указано». */
    weather: z.preprocess((v) => (v === undefined ? null : v), SiteWeather.nullable()),
  })
  .superRefine((draft, ctx) => {
    const ids = new Set<string>();
    const lineIds = new Set(draft.lines.map((line) => line.id));
    for (const id of draft.lines.flatMap((line) => [
      line.id,
      ...line.labor.map((x) => x.id),
      ...line.machines.map((x) => x.id),
      ...line.materials.map((x) => x.id),
    ])) {
      if (ids.has(id))
        ctx.addIssue({ code: 'custom', message: 'Повторяется идентификатор строки', path: ['lines'] });
      ids.add(id);
    }
    const photos = new Set<string>();
    for (const photo of draft.photos) {
      if (!lineIds.has(photo.lineId))
        ctx.addIssue({ code: 'custom', message: 'Фото относится к отсутствующей работе', path: ['photos'] });
      if (photos.has(photo.clientPhotoId))
        ctx.addIssue({ code: 'custom', message: 'Фото добавлено дважды', path: ['photos'] });
      photos.add(photo.clientPhotoId);
    }
    try {
      new Intl.DateTimeFormat('ru', { timeZone: draft.projectTimeZone });
    } catch {
      ctx.addIssue({
        code: 'custom',
        message: 'Неизвестный часовой пояс проекта',
        path: ['projectTimeZone'],
      });
    }
  });
export type SiteReportDraft = z.infer<typeof SiteReportDraft>;

export const SiteVerification = z.strictObject({
  lineId: Id,
  quantity: SiteQuantity,
  reason: Note,
  resourcesConfirmed: z.literal(true),
});
export type SiteVerification = z.infer<typeof SiteVerification>;
export const SiteReportAction = z.discriminatedUnion('action', [
  z.strictObject({ action: z.literal('save'), draft: SiteReportDraft }),
  z.strictObject({ action: z.literal('submit') }),
  z.strictObject({ action: z.literal('verify'), lines: z.array(SiteVerification).min(1).max(200) }),
  z.strictObject({ action: z.literal('return'), reason: Note.min(1) }),
  z.strictObject({ action: z.literal('reject'), reason: Note.min(1) }),
  z.strictObject({ action: z.literal('accept') }),
  /** «Открыть снова» принятый отчёт (ПР-19): руководитель проекта с причиной; факт графика и затраты снимаются */
  z.strictObject({ action: z.literal('reopen'), reason: Note.min(3) }),
]);
export type SiteReportAction = z.infer<typeof SiteReportAction>;
const CommandFields = {
  schemaVersion: z.literal(1),
  clientId: Id,
  clientOperationId: Id,
  expectedRevision: Revision,
  recordedOffline: z.boolean(),
  sourceRevisions: z.array(SourceRef).max(500),
  occurredAt: z.iso.datetime({ offset: true }),
  effectiveAt: SiteDate,
};
export const SiteCreateReportInput = z.strictObject({
  ...CommandFields,
  expectedRevision: z.literal(0),
  draft: SiteReportDraft,
});
export const SiteSaveReportInput = z.strictObject({ ...CommandFields, draft: SiteReportDraft });
export const SiteSubmitReportInput = z.strictObject(CommandFields);
export const SiteReviewReportInput = z.discriminatedUnion('action', [
  z.strictObject({
    ...CommandFields,
    action: z.literal('verify'),
    lines: z.array(SiteVerification).min(1).max(200),
  }),
  z.strictObject({ ...CommandFields, action: z.literal('return'), reason: Note.min(1) }),
  z.strictObject({ ...CommandFields, action: z.literal('reject'), reason: Note.min(1) }),
]);
export const SiteAcceptReportInput = z.strictObject(CommandFields);
export const SiteReopenReportInput = z.strictObject({ ...CommandFields, reason: Note.min(3) });
/** Внутреннее представление команды; HTTP использует отдельные маршруты через siteHttpCommand. */
export const SiteCommand = z.strictObject({
  ...CommandFields,
  payload: SiteReportAction,
});
export type SiteCommand = z.infer<typeof SiteCommand>;

export function assertSiteIdempotencyKey(clientOperationId: string, header: string) {
  if (Id.parse(header) !== clientOperationId)
    throw new Error('Idempotency-Key не совпадает с clientOperationId');
}

export function siteHttpCommand(input: SiteCommand, reportId: string) {
  const { payload, ...envelope } = SiteCommand.parse(input);
  const path = `/api/v1/site/reports/${Id.parse(reportId)}`;
  switch (payload.action) {
    case 'save':
      return {
        method: 'PATCH' as const,
        path,
        body: SiteSaveReportInput.parse({ ...envelope, draft: payload.draft }),
      };
    case 'submit':
      return { method: 'POST' as const, path: `${path}/submit`, body: SiteSubmitReportInput.parse(envelope) };
    case 'accept':
      return { method: 'POST' as const, path: `${path}/accept`, body: SiteAcceptReportInput.parse(envelope) };
    case 'reopen':
      return {
        method: 'POST' as const,
        path: `${path}/reopen`,
        body: SiteReopenReportInput.parse({ ...envelope, reason: payload.reason }),
      };
    default:
      return {
        method: 'POST' as const,
        path: `${path}/review`,
        body: SiteReviewReportInput.parse({ ...envelope, ...payload }),
      };
  }
}
export type SiteWorkflowState = 'draft' | 'submitted' | 'verified' | 'returned' | 'rejected' | 'accepted';
export type SiteSyncState = 'pending' | 'sent' | 'conflict' | 'rejected';

export interface SiteReportSnapshot {
  id: string;
  tenantId: string;
  authorId: string;
  clientId: string;
  revision: number;
  state: SiteWorkflowState;
  draft: SiteReportDraft;
  verification: SiteVerification[] | null;
  accepted: SiteVerification[] | null;
  updatedAt: string;
  decision: { actorId: string; action: SiteReportAction['action']; reason: string | null } | null;
  /** Снимок связей draft; при submit фиксируется опубликованный состав. null/undefined — Q7 не подключён. */
  remarkManifest?: ReadonlyArray<SiteFrozenRemarkReference> | null;
}

/** Структурный стык с Q7 SiteRemarkVersionReference; изменяемый master принадлежит qa. */
export interface SiteFrozenRemarkReference {
  schemaVersion: 1;
  reportId: string;
  reportRevision: number;
  lineId: string;
  linkId: string;
  linkRevision: number;
  remarkId: string;
}

/** Передаётся в core/schedule из доверенной транзакции, не принимается от браузера. */
export interface SiteAcceptedFact {
  sourceType: 'site.report';
  sourceId: string;
  sourceRevision: number;
  reportId: string;
  reportRevision: number;
  projectId: string;
  tenantId: string;
  acceptedBy: string;
  acceptedAt: string;
  quantityMode: 'period_increment';
  reportDay: string;
  projectTimeZone: string;
  lines: Array<{ sourceLineId: string; lineId: string; activityId: string; quantity: string; unit: string }>;
  sourceSnapshot: SiteReportSnapshot;
}
